import { type KeyboardEvent } from 'react';

export type StatusFilterChipOption<T extends string> = {
  value: T;
  label: string;
};

type StatusFilterChipRowProps<T extends string> = {
  options: StatusFilterChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
};

const chipBaseClassName =
  'min-h-9 shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm';

const chipActiveClassName = 'bg-primary-600 text-white';

const chipInactiveClassName =
  'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:ring-gray-700 dark:hover:bg-gray-800';

export const StatusFilterChipRow = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className = '',
}: StatusFilterChipRowProps<T>) => {
  const handleChipKeyDown = (event: KeyboardEvent<HTMLButtonElement>, optionValue: T) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    onChange(optionValue);
  };

  const wrapperClassName = ['flex flex-nowrap gap-2 overflow-x-auto pt-0.5 pb-1', className].filter(Boolean).join(' ');

  const resolveChipEdgeClassName = (index: number) => {
    const isFirst = index === 0;
    const isLast = index === options.length - 1;
    return [isFirst ? 'ml-1' : '', isLast ? 'mr-1' : ''].filter(Boolean).join(' ');
  };

  return (
    <div className={wrapperClassName} role="tablist" aria-label={ariaLabel}>
      {options.map((option, index) => {
        const isSelected = value === option.value;
        const edgeClassName = resolveChipEdgeClassName(index);
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isSelected}
            tabIndex={0}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleChipKeyDown(event, option.value)}
            className={`${chipBaseClassName} ${edgeClassName} ${isSelected ? chipActiveClassName : chipInactiveClassName}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
